import React, { useContext } from 'react';
import { Edit, Trash2, Search, ArrowUp, ArrowDown } from 'lucide-react';
import { formatDateTime, getPriorityColor } from '../utils/otBookingUtils';
import { AuthContext } from '../../../../contexts/AuthContext';

const BookingTable = ({ bookings, onEdit, onDelete, onClearFilters, searchTerm, startDate, endDate, sortConfig, requestSort }) => {
  const { userRole, permissions } = useContext(AuthContext);
  const hasPermission = (perm) => (userRole || '').toLowerCase() === 'admin' || (permissions || []).includes(perm);
  const showActionsColumn = hasPermission('OT Booking Edit') || hasPermission('OT Booking Delete');

  const getSortIcon = (key) => {
    if (sortConfig?.key !== key) return null;
    return sortConfig.direction === 'asc' ? <ArrowUp className="h-3 w-3 inline ml-1" /> : <ArrowDown className="h-3 w-3 inline ml-1" />;
  };

  const headers = [
    { label: 'UHID', key: 'uhid_no' },
    { label: 'Patient Name', key: 'patient_name' },
    { label: 'Room No', key: 'room_no' },
    { label: 'Booking Date', key: 'booking_datetime' },
    { label: 'Surgery Type', key: 'type_of_surgery' },
    { label: 'Surgical Code', key: 'surgical_code' },
    { label: 'Priority', key: 'priority_status' }
  ];

  return (
    <div className="hidden lg:block bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead>
            <tr className="bg-teal-50 border-b border-teal-100">
              {headers.map(h => (
                <th 
                  key={h.key} 
                  onClick={() => requestSort(h.key)}
                  className="text-left p-4 font-semibold text-teal-900 cursor-pointer hover:bg-teal-100/50 transition-colors"
                >
                  <div className="flex items-center">
                    {h.label} {getSortIcon(h.key)}
                  </div>
                </th>
              ))}
              {showActionsColumn && <th className="text-center p-4 font-semibold text-teal-900">Actions</th>}
            </tr>
          </thead>
          <tbody>
            {bookings.length === 0 ? (
              <tr>
                <td colSpan={showActionsColumn ? 8 : 7} className="p-12 text-center">
                  <div className="flex flex-col items-center">
                    <Search className="h-16 w-16 text-gray-300 mb-4" />
                    <h3 className="text-lg font-medium text-gray-500 mb-2">No bookings found</h3>
                    <p className="text-gray-400 mb-4">Try adjusting your search criteria</p>
                    {(searchTerm || startDate || endDate) && (
                      <button
                        onClick={onClearFilters}
                        className="text-teal-600 hover:text-teal-700 font-medium"
                      >
                        Clear filters
                      </button>
                    )}
                  </div>
                </td>
              </tr>
            ) : (
              bookings.map((booking, index) => {
                const { date, time } = formatDateTime(booking.booking_datetime);
                return (
                  <tr 
                    key={booking.uhid_no} 
                    className={`border-b border-gray-100 hover:bg-teal-50/50 transition-colors ${
                      index % 2 === 0 ? 'bg-white' : 'bg-gray-50/30'
                    }`}
                  >
                    <td className="p-4">
                      <span className="bg-gray-100 text-gray-700 px-2 py-1 rounded font-mono text-sm">
                        {booking.uhid_no}
                      </span>
                    </td>
                    <td className="p-4">
                      <div className="font-medium text-gray-900">{booking.patient_name}</div>
                      <div className="text-sm text-gray-500">{booking.mobile}</div>
                    </td>
                    <td className="p-4">
                      <span className="bg-blue-100 text-blue-800 px-2 py-1 rounded text-sm font-medium">
                        {booking.room_no}
                      </span>
                    </td>
                    <td className="p-4 text-gray-900">{date} at {time}</td>
                    <td className="p-4">
                      <div className="text-gray-900">{booking.type_of_surgery}</div>
                    </td>
                    <td className="p-4">
                      <span className="bg-purple-100 text-purple-800 px-2 py-1 rounded text-sm font-mono">
                        {booking.surgical_code}
                      </span>
                    </td>
                    <td className="p-4">
                      <span className={`px-2 py-1 rounded text-sm font-medium ${getPriorityColor(booking.priority_status)}`}>
                        {booking.priority_status}
                      </span>
                    </td>
                    {showActionsColumn && (
                      <td className="p-4">
                        <div className="flex justify-center gap-2">
                          {hasPermission('OT Booking Edit') && (
                            <button 
                              onClick={() => onEdit(booking.uhid_no)} 
                              className="flex items-center gap-2 px-3 py-2 text-sm text-blue-600 hover:bg-blue-50 border border-blue-200 rounded-lg transition-colors font-medium"
                              title="Edit Booking"
                            >
                              <Edit className="h-4 w-4" />
                              Edit
                            </button>
                          )}
                        
                          {hasPermission('OT Booking Delete') && (
                            <button 
                              onClick={() => onDelete(booking.uhid_no)} 
                              className="flex items-center gap-2 px-3 py-2 text-sm text-red-600 hover:bg-red-50 border border-red-200 rounded-lg transition-colors font-medium"
                              title="Delete Booking"
                            >
                              <Trash2 className="h-4 w-4" />
                              Delete
                            </button>
                          )}
                        </div>
                      </td>
                    )}
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default BookingTable;
